import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Checkbox,
  Toolbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@src/api/clients';
import { Link, useSearchParams } from 'react-router-dom';
import { DateRangePicker, VerticalDropdown, TerritoryDropdown } from '@src/components';
import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import useParentCompanyAutocomplete from '@src/hooks/useParentCompanyAutocomplete';
import { formatDateToYMD, getCurrentMonthDates } from '@src/utils/dateUtils';

const sanitizeFilters = (filters = {}) =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );

const LEAD_LIST_FIELDS = [
  'id',
  'client_name',
  'date_created',
  'webhook_sent_datetime',
  'first_name',
  'last_name',
  'phone',
  'email',
  'source',
  'territory',
  'zip_code',
  'self_book_appointment_date_time',
  'vertical',
  'processed',
  'success_post',
  'post_response',
  'last_webhook_status_code',
].join(',');

const LEAD_LIST_STORAGE_KEY = 'leadListTableState:v1';
const LEAD_LIST_CACHE_LIMIT = 25;

const parseYmdToDate = (value, fallback) => {
  if (!value) return fallback;
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

const readLeadListStorage = ({ defaultStart, defaultEnd, clientFromQuery = '', deliveryFromQuery = '' }) => {
  const defaultFilters = {
    client: clientFromQuery,
    parent_company_id: '',
    phone: '',
    email: '',
    zip_code: '',
    vertical: '',
    territory: '',
    success_post: '',
    delivery: deliveryFromQuery,
    date_after: formatDateToYMD(defaultStart),
    date_before: formatDateToYMD(defaultEnd),
  };

  if (typeof window === 'undefined') {
    return {
      filters: defaultFilters,
      page: 1,
      leads: [],
      totalPages: 1,
      totalCount: 0,
      startDate: defaultStart,
      endDate: defaultEnd,
      cacheEntries: [],
    };
  }

  try {
    const raw = window.sessionStorage.getItem(LEAD_LIST_STORAGE_KEY);
    if (!raw) {
      return {
        filters: defaultFilters,
        page: 1,
        leads: [],
        totalPages: 1,
        totalCount: 0,
        startDate: defaultStart,
        endDate: defaultEnd,
        cacheEntries: [],
      };
    }
    const parsed = JSON.parse(raw) || {};
    const parsedFilters = { ...defaultFilters, ...(parsed.filters || {}) };
    if (clientFromQuery) {
      parsedFilters.client = clientFromQuery;
    }
    if (deliveryFromQuery) {
      parsedFilters.delivery = deliveryFromQuery;
    }
    const startDate = parseYmdToDate(parsed.startDate, defaultStart);
    const endDate = parseYmdToDate(parsed.endDate, defaultEnd);
    parsedFilters.date_after = formatDateToYMD(startDate);
    parsedFilters.date_before = formatDateToYMD(endDate);

    return {
      filters: parsedFilters,
      page: Number(parsed.page) > 0 ? Number(parsed.page) : 1,
      leads: Array.isArray(parsed.leads) ? parsed.leads : [],
      totalPages: Number(parsed.totalPages) > 0 ? Number(parsed.totalPages) : 1,
      totalCount: Number(parsed.totalCount) >= 0 ? Number(parsed.totalCount) : 0,
      startDate,
      endDate,
      cacheEntries: Array.isArray(parsed.cacheEntries) ? parsed.cacheEntries : [],
    };
  } catch (error) {
    console.error('Failed to parse lead list cache', error);
    return {
      filters: defaultFilters,
      page: 1,
      leads: [],
      totalPages: 1,
      totalCount: 0,
      startDate: defaultStart,
      endDate: defaultEnd,
      cacheEntries: [],
    };
  }
};

export default function LeadListPage() {
  const { state } = useAuth();
  const user = state?.user;
  const memberships = Array.isArray(user?.client_memberships) ? user.client_memberships : [];
  const isClientMember = !user?.is_staff && !user?.is_superuser && memberships.length > 0;
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const forcedClientId = isClientMember && memberships[0]?.client_id
    ? String(memberships[0].client_id)
    : '';
  const [searchParams] = useSearchParams();
  const [defaultStart, defaultEnd] = useMemo(() => getCurrentMonthDates(), []);
  const clientFromQuery = forcedClientId || searchParams.get('client_id') || '';
  // Deep-link from Data Health alerts, e.g. /leads?client_id=42&delivery=dead
  // opens exactly the dead-lettered leads behind a delivery anomaly.
  const deliveryFromQuery = searchParams.get('delivery') || '';
  const persistedState = useMemo(
    () => readLeadListStorage({ defaultStart, defaultEnd, clientFromQuery, deliveryFromQuery }),
    [defaultStart, defaultEnd, clientFromQuery, deliveryFromQuery]
  );
  const [leads, setLeads] = useState(() => persistedState.leads);
  const [page, setPage] = useState(() => persistedState.page);
  const [totalPages, setTotalPages] = useState(() => persistedState.totalPages);
  const [totalCount, setTotalCount] = useState(() => persistedState.totalCount);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => persistedState.filters);
  const [startDate, setStartDate] = useState(() => persistedState.startDate);
  const [endDate, setEndDate] = useState(() => persistedState.endDate);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [updatingVertical, setUpdatingVertical] = useState(false);
  const [updatingSuccessPost, setUpdatingSuccessPost] = useState(false);
  const [resendingLeads, setResendingLeads] = useState(false);
  const [bulkVertical, setBulkVertical] = useState('');
  const [bulkSuccessPost, setBulkSuccessPost] = useState('');
  const [exportStatusText, setExportStatusText] = useState('');
  const [exportError, setExportError] = useState('');
  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete({ parentCompanyId: filters.parent_company_id || undefined });
  // Parent-company filter is staff-only; client members are already scoped to
  // their own brand/clients server-side.
  const {
    options: parentCompanyOptions,
    loading: parentCompanyLoading,
    findOptionByValue: findParentCompanyByValue,
  } = useParentCompanyAutocomplete({ skip: !isStaff });
  const leadRequestCacheRef = useRef(new Map(persistedState.cacheEntries));
  const leadInFlightRequestRef = useRef(new Map());
  const backendHydrationSeqRef = useRef(0);
  const [cacheVersion, setCacheVersion] = useState(0);

  const pageSize = 50;

  const buildLeadsUrl = useCallback((pageParam = page, activeFilters = filters) => {
    const { phone, email, ...restFilters } = activeFilters;
    const search = phone?.trim() || email?.trim() || '';
    const cleanedFilters = Object.fromEntries(
      Object.entries(restFilters)
        .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
        .filter(([_, v]) => v !== '')
    );
    const params = new URLSearchParams({
      page: pageParam,
      page_size: pageSize,
      fields: LEAD_LIST_FIELDS,
      ...cleanedFilters,
    });
    if (search) {
      params.set('search', search);
    }
    return `dashboard/leads/?${params.toString()}`;
  }, [filters, page]);

  const fetchCachedLeadPage = useCallback(async (url, { force = false } = {}) => {
    if (!url) {
      return { rows: [], count: 0 };
    }

    if (!force && leadRequestCacheRef.current.has(url)) {
      return leadRequestCacheRef.current.get(url);
    }

    if (!force && leadInFlightRequestRef.current.has(url)) {
      return leadInFlightRequestRef.current.get(url);
    }

    const requestPromise = api
      .request({ url, method: 'GET' })
      .then((res) => {
        const payload = {
          rows: res?.data?.results || [],
          count: Number(res?.data?.count || 0),
        };
        leadRequestCacheRef.current.set(url, payload);
        if (leadRequestCacheRef.current.size > LEAD_LIST_CACHE_LIMIT) {
          const firstKey = leadRequestCacheRef.current.keys().next().value;
          leadRequestCacheRef.current.delete(firstKey);
        }
        setCacheVersion((prev) => prev + 1);
        return payload;
      })
      .finally(() => {
        leadInFlightRequestRef.current.delete(url);
      });

    leadInFlightRequestRef.current.set(url, requestPromise);
    return requestPromise;
  }, []);

  const hydrateLeadBackendData = useCallback(async (leadRows = []) => {
    const leadIds = leadRows.map((row) => row?.id).filter(Boolean);
    if (!leadIds.length) {
      return;
    }
    const requestSeq = backendHydrationSeqRef.current + 1;
    backendHydrationSeqRef.current = requestSeq;
    try {
      const res = await api.request({
        url: 'dashboard/leads/backend-data-batch/',
        method: 'POST',
        payload: { lead_ids: leadIds },
      });
      if (backendHydrationSeqRef.current !== requestSeq) {
        return;
      }
      const relatedById = new Map(
        (res?.data?.results || []).map((item = {}) => [
          String(item.id),
          item.related_backend_data || [],
        ])
      );
      setLeads((prev) =>
        prev.map((lead = {}) => ({
          ...lead,
          related_backend_data: relatedById.get(String(lead.id)) || [],
        }))
      );
    } catch (err) {
      console.error('Error hydrating lead backend data:', err);
    }
  }, []);

  const fetchLeads = async (pageParam = page, activeFilters = filters, { force = false } = {}) => {
    setLoading(true);
    try {
      const url = buildLeadsUrl(pageParam, activeFilters);
      const { rows, count } = await fetchCachedLeadPage(url, { force });
      setLeads(rows);
      setTotalCount(count);
      setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
      hydrateLeadBackendData(rows);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.sessionStorage.setItem(
        LEAD_LIST_STORAGE_KEY,
        JSON.stringify({
          filters,
          page,
          leads,
          totalPages,
          totalCount,
          startDate: formatDateToYMD(startDate),
          endDate: formatDateToYMD(endDate),
          cacheEntries: Array.from(leadRequestCacheRef.current.entries()),
          updatedAt: Date.now(),
        })
      );
    } catch (error) {
      console.error('Failed to persist lead list cache', error);
    }
  }, [filters, page, leads, totalPages, totalCount, startDate, endDate, cacheVersion]);
  
    useEffect(() => {
      fetchLeads(page);
    }, [page]);

  const handleSearch = () => {
    const newPage = 1;
    setPage(newPage);
    setSelectAllFiltered(false);
    setSelectedIds([]);
    fetchLeads(newPage, filters, { force: true });
  };

  const handleInputChange = (field, value) => {
    if (field === 'client' && forcedClientId) {
      setFilters((prev) => ({ ...prev, client: forcedClientId }));
      return;
    }
    // Territory options are client-scoped, so drop any stale selection when the
    // client changes.
    if (field === 'client') {
      setFilters((prev) => ({ ...prev, client: value, territory: '' }));
      return;
    }
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Switching parent company re-scopes the client dropdown, so drop any stale
  // client/territory selection. Applies on Search like the other dropdowns.
  const handleParentCompanyChange = (option) => {
    const value = option?.value ? String(option.value) : '';
    setFilters((prev) => ({ ...prev, parent_company_id: value, client: '', territory: '' }));
  };

  useEffect(() => {
    if (!clientFromQuery || filters.client === clientFromQuery) {
      return;
    }
    setFilters((prev) => ({ ...prev, client: clientFromQuery }));
    setPage(1);
    setSelectAllFiltered(false);
    setSelectedIds([]);
  }, [clientFromQuery, filters.client]);

  useEffect(() => {
    if (!deliveryFromQuery || filters.delivery === deliveryFromQuery) {
      return;
    }
    setFilters((prev) => ({ ...prev, delivery: deliveryFromQuery }));
    setPage(1);
    setSelectAllFiltered(false);
    setSelectedIds([]);
  }, [deliveryFromQuery, filters.delivery]);

  const handleDateRangeChange = ({ selection }) => {
    const { startDate: nextStart, endDate: nextEnd } = selection;
    setStartDate(nextStart);
    setEndDate(nextEnd);

    const formattedStart = nextStart ? formatDateToYMD(nextStart) : '';
    const formattedEnd = nextEnd ? formatDateToYMD(nextEnd) : '';

    setFilters((prev) => {
      const nextFilters = { ...prev, date_after: formattedStart, date_before: formattedEnd };
      if (page === 1) {
        fetchLeads(1, nextFilters, { force: true });
      }
      return nextFilters;
    });

    if (page !== 1) {
      setPage(1);
    }
    setSelectAllFiltered(false);
    setSelectedIds([]);
  };

  const postResponseCellSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 260,
  };
  const phoneCellSx = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
  const searchFieldBaseSx = { '& .MuiInputBase-root': { height: 40 } };
  const selectFieldBaseSx = {
    '& .MuiInputBase-root': { height: 40, display: 'flex', alignItems: 'center' },
    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0 },
  };

  const stringifyValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  };

  const truncate = (value, maxLength = 120) => {
    if (!value) return '-';
    return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
  };

  // HTTP status from the client's last webhook response: 2xx accepted (green),
  // 4xx received-but-declined e.g. duplicate (amber), 5xx/none delivery failure (red).
  const statusCodeColor = (code) => {
    if (code == null) return 'text.secondary';
    if (code >= 200 && code < 300) return 'success.main';
    if (code >= 400 && code < 500) return 'warning.main';
    return 'error.main';
  };

  const isRowSelected = (id) => selectAllFiltered || selectedIds.includes(id);
  const currentPageIds = leads.map((lead) => lead.id);
  const allOnPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => isRowSelected(id));

  const handleToggleRow = (id) => {
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      setSelectedIds([id]);
      return;
    }
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleToggleAllCurrent = () => {
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      setSelectedIds([]);
      return;
    }

    if (allOnPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      const combined = new Set([...selectedIds, ...currentPageIds]);
      setSelectedIds(Array.from(combined));
    }
  };

  const handleSelectAllFiltered = () => {
    setSelectAllFiltered(true);
    setSelectedIds([]);
  };

  const handleClearSelection = () => {
    setSelectAllFiltered(false);
    setSelectedIds([]);
  };

  const exportLeads = async () => {
    try {
      setExportError('');
      setExportStatusText('');
      // Client members (incl. parent-company contacts) are always scoped to
      // their own clients server-side, so a selection is optional. Staff can
      // query every client, so they must pick a client OR a parent company to
      // avoid an unbounded export.
      if (!isClientMember && !filters.client && !filters.parent_company_id) {
        setExportError('Select a client or parent company before exporting leads.');
        return;
      }
      const exportAllFiltered = isClientMember || selectAllFiltered;
      if (!exportAllFiltered && selectedIds.length === 0) {
        return;
      }

      const baseFilters = (() => {
        const { phone, email, ...rest } = filters;
        const search = phone?.trim() || email?.trim() || '';
        const cleaned = sanitizeFilters(rest);
        if (search) {
          cleaned.search = search;
        }
        return cleaned;
      })();
      setExporting(true);
      setExportStatusText('Preparing lead export…');

      const startRes = await api.request({
        url: 'dashboard/leads/export/',
        method: 'POST',
        payload: {
          filters: baseFilters,
          select_all_filtered: exportAllFiltered,
          selected_ids: exportAllFiltered ? [] : selectedIds,
        },
      });
      const exportId = startRes?.data?.id;
      if (!exportId) {
        throw new Error('Failed to start lead export.');
      }

      setExportStatusText('Export is processing…');
      let statusData = null;
      for (let attempt = 0; attempt < 180; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        const statusRes = await api.request({
          url: `dashboard/leads/exports/${exportId}/status/`,
          method: 'GET',
        });
        statusData = statusRes?.data || null;
        if (statusData?.status === 'succeeded') break;
        if (statusData?.status === 'failed') {
          throw new Error(statusData?.error_message || 'Lead export failed.');
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      if (!statusData || statusData.status !== 'succeeded' || !statusData.download_url) {
        throw new Error('Lead export timed out. Please try again.');
      }

      const downloadRes = await api.request({
        url: `dashboard/leads/exports/${exportId}/download/`,
        method: 'GET',
        options: { responseType: 'blob' },
      });
      const disposition = downloadRes?.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^\";]+)/i);
      const filename = filenameMatch
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim())
        : `leads-export-${exportId}.csv`;
      const blob = downloadRes?.data instanceof Blob
        ? downloadRes.data
        : new Blob([downloadRes?.data], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      setExportStatusText('Export ready. Download started.');
    } catch (err) {
      console.error('Error exporting leads:', err);
      setExportError(err?.message || 'Failed to export leads.');
    } finally {
      setExporting(false);
    }
  };

  const updateSelectedLeadVertical = async () => {
    try {
      setExportError('');
      setExportStatusText('');
      if (!bulkVertical) {
        setExportError('Select a vertical before applying bulk edit.');
        return;
      }
      if (!selectAllFiltered && selectedIds.length === 0) {
        setExportError('Select at least one lead first.');
        return;
      }

      const baseFilters = (() => {
        const { phone, email, ...rest } = filters;
        const search = phone?.trim() || email?.trim() || '';
        const cleaned = sanitizeFilters(rest);
        if (search) {
          cleaned.search = search;
        }
        return cleaned;
      })();

      setUpdatingVertical(true);
      const res = await api.request({
        url: 'dashboard/leads/bulk-update/',
        method: 'POST',
        payload: {
          filters: baseFilters,
          select_all_filtered: selectAllFiltered,
          selected_ids: selectAllFiltered ? [] : selectedIds,
          vertical: bulkVertical,
        },
      });

      const updatedCount = Number(res?.data?.updated_count || 0);
      setExportStatusText(`Updated vertical on ${updatedCount} lead${updatedCount === 1 ? '' : 's'}.`);
      setSelectAllFiltered(false);
      setSelectedIds([]);
      fetchLeads(page, filters, { force: true });
    } catch (err) {
      console.error('Error bulk-updating lead vertical:', err);
      setExportError(err?.response?.data?.detail || err?.message || 'Failed to update selected leads.');
    } finally {
      setUpdatingVertical(false);
    }
  };

  const updateSelectedLeadSuccessPost = async () => {
    try {
      setExportError('');
      setExportStatusText('');
      if (bulkSuccessPost === '') {
        setExportError('Choose Success Post = Yes or No before applying bulk edit.');
        return;
      }
      if (!selectAllFiltered && selectedIds.length === 0) {
        setExportError('Select at least one lead first.');
        return;
      }

      const baseFilters = (() => {
        const { phone, email, ...rest } = filters;
        const search = phone?.trim() || email?.trim() || '';
        const cleaned = sanitizeFilters(rest);
        if (search) {
          cleaned.search = search;
        }
        return cleaned;
      })();

      setUpdatingSuccessPost(true);
      const successPostValue = bulkSuccessPost === 'true';
      const res = await api.request({
        url: 'dashboard/leads/bulk-update/',
        method: 'POST',
        payload: {
          filters: baseFilters,
          select_all_filtered: selectAllFiltered,
          selected_ids: selectAllFiltered ? [] : selectedIds,
          success_post: successPostValue,
        },
      });

      const updatedCount = Number(res?.data?.updated_count || 0);
      setExportStatusText(
        `Updated Success Post (${successPostValue ? 'Yes' : 'No'}) on ${updatedCount} lead${
          updatedCount === 1 ? '' : 's'
        }.`
      );
      setSelectAllFiltered(false);
      setSelectedIds([]);
      fetchLeads(page, filters, { force: true });
    } catch (err) {
      console.error('Error bulk-updating lead success post:', err);
      setExportError(err?.response?.data?.detail || err?.message || 'Failed to update selected leads.');
    } finally {
      setUpdatingSuccessPost(false);
    }
  };

  const resendSelectedLeads = async () => {
    try {
      setExportError('');
      setExportStatusText('');
      if (!selectAllFiltered && selectedIds.length === 0) {
        setExportError('Select at least one lead first.');
        return;
      }

      const targetCount = selectAllFiltered ? totalCount || 0 : selectedIds.length;
      const confirmed = window.confirm(
        `Re-push ${targetCount} lead${targetCount === 1 ? '' : 's'} to their client webhook` +
          `${targetCount === 1 ? '' : 's'}? This clears the "Webhook Sent" stamp so the lead` +
          ' is re-delivered to the client within a few minutes.'
      );
      if (!confirmed) {
        return;
      }

      const baseFilters = (() => {
        const { phone, email, ...rest } = filters;
        const search = phone?.trim() || email?.trim() || '';
        const cleaned = sanitizeFilters(rest);
        if (search) {
          cleaned.search = search;
        }
        return cleaned;
      })();

      setResendingLeads(true);
      const res = await api.request({
        url: 'dashboard/leads/reset-webhook/',
        method: 'POST',
        payload: {
          filters: baseFilters,
          select_all_filtered: selectAllFiltered,
          selected_ids: selectAllFiltered ? [] : selectedIds,
        },
      });

      const updatedCount = Number(res?.data?.updated_count || 0);
      setExportStatusText(
        `Queued ${updatedCount} lead${updatedCount === 1 ? '' : 's'} for re-delivery. ` +
          'The webhook task will re-push them within a few minutes.'
      );
      setSelectAllFiltered(false);
      setSelectedIds([]);
      fetchLeads(page, filters, { force: true });
    } catch (err) {
      console.error('Error resending selected leads:', err);
      setExportError(err?.response?.data?.detail || err?.message || 'Failed to resend selected leads.');
    } finally {
      setResendingLeads(false);
    }
  };

  // Auto-filter when client (primary filter) changes.
  useEffect(() => {
    if (!filters.client) {
      return;
    }
    const newPage = 1;
    setPage(newPage);
    setSelectAllFiltered(false);
    setSelectedIds([]);
    fetchLeads(newPage, filters, { force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.client]);

    return (
      <Box p={3}>
        <Card>
          <CardHeader title="Leads" />
          <Divider />
          <CardContent>
            <Box
            sx={(theme) => ({
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2.5,
              mb: 2,
              bgcolor: theme.palette.background.level1 ?? theme.palette.background.default,
              boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)',
            })}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                    Filters & search
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Narrow results by client, status, and contact details.
                  </Typography>
                </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Date & status filters
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={2}
                  alignItems="flex-start"
                >
                  <Box
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      p: 1.25,
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      minWidth: { xs: '100%', sm: 320 },
                      maxWidth: { sm: 420 },
                      bgcolor: theme.palette.background.paper,
                    })}
                  >
                    <Box sx={{ minWidth: { xs: '100%', sm: 260 }, width: '100%' }}>
                      <Stack spacing={0.25}>
                        <Typography variant="caption" color="text.secondary">
                          Date created
                        </Typography>
                        <DateRangePicker
                          startDate={startDate}
                          endDate={endDate}
                          onChange={handleDateRangeChange}
                          style={{ width: '100%' }}
                        />
                      </Stack>
                    </Box>
                  </Box>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    alignItems="center"
                    sx={{ flex: 1 }}
                  >
                    {isStaff ? (
                      <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                        <Autocomplete
                          size="small"
                          options={parentCompanyOptions}
                          loading={parentCompanyLoading}
                          value={findParentCompanyByValue(filters.parent_company_id) || null}
                          onChange={(_, option) => handleParentCompanyChange(option)}
                          isOptionEqualToValue={(option, value) =>
                            String(option.value) === String(value.value)
                          }
                          getOptionLabel={(option) => option?.label || ''}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Parent Company"
                              placeholder="All parent companies"
                              sx={searchFieldBaseSx}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {parentCompanyLoading ? (
                                      <CircularProgress color="inherit" size={16} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>
                    ) : null}
                    <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                      <Autocomplete
                        size="small"
                        options={clientOptions}
                        loading={clientOptionsLoading}
                        value={findClientOptionByValue(filters.client) || null}
                        onChange={(_, option) =>
                          handleInputChange('client', option?.value || '')
                        }
                        onInputChange={handleClientSearch}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        getOptionLabel={(option) => option?.label || ''}
                        disabled={Boolean(forcedClientId)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Client"
                            placeholder={forcedClientId ? 'Locked to your client' : 'Search clients'}
                            sx={searchFieldBaseSx}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {!forcedClientId && clientOptionsLoading ? (
                                    <CircularProgress color="inherit" size={16} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                      <VerticalDropdown
                        valueType="name"
                        value={filters.vertical}
                        onChange={(value) => handleInputChange('vertical', value)}
                      />
                    </Box>
                    <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                      <TerritoryDropdown
                        clientId={filters.client}
                        value={filters.territory}
                        onChange={(value) => handleInputChange('territory', value)}
                      />
                    </Box>
                    <FormControl
                      size="small"
                      sx={{ minWidth: { xs: '100%', sm: 200 }, ...selectFieldBaseSx }}
                    >
                      <InputLabel>Success Post</InputLabel>
                      <Select
                        value={filters.success_post}
                        onChange={(e) => handleInputChange('success_post', e.target.value)}
                        label="Success Post"
                      >
                        <MenuItem value=""><em>Any</em></MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl
                      size="small"
                      sx={{ minWidth: { xs: '100%', sm: 220 }, ...selectFieldBaseSx }}
                    >
                      <InputLabel>Delivery</InputLabel>
                      <Select
                        value={filters.delivery || ''}
                        onChange={(e) => handleInputChange('delivery', e.target.value)}
                        label="Delivery"
                      >
                        <MenuItem value=""><em>Any</em></MenuItem>
                        <MenuItem value="dead">Dead-lettered (gave up)</MenuItem>
                        <MenuItem value="undelivered">Undelivered (no webhook)</MenuItem>
                        <MenuItem value="declined">Declined (client rule)</MenuItem>
                        <MenuItem value="rejected">Rejected (sent, failed)</MenuItem>
                        <MenuItem value="delivered">Delivered</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>
              </Stack>

              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1.5}
                  alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                >
                  <TextField
                    label="Phone"
                    value={filters.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    size="small"
                    sx={{ minWidth: { xs: '100%', sm: 200 }, ...searchFieldBaseSx }}
                  />
                  <TextField
                    label="Email"
                    value={filters.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    size="small"
                    sx={{ minWidth: { xs: '100%', sm: 220 }, ...searchFieldBaseSx }}
                  />
                  <TextField
                    label="ZIP Code"
                    value={filters.zip_code}
                    onChange={(e) => handleInputChange('zip_code', e.target.value)}
                    size="small"
                    sx={{ minWidth: { xs: '100%', sm: 160 }, ...searchFieldBaseSx }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      sx={{ height: 40, minWidth: 96 }}
                    >
                      Search
                    </Button>
                  </Box>
                </Stack>
              </Stack>
            </Box>

            {!isClientMember ? (
              <Toolbar
                sx={{
                  px: 0,
                  mb: 1,
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {selectAllFiltered
                    ? `All ${totalCount || 0} filtered leads selected`
                    : `${selectedIds.length} selected`}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                    <VerticalDropdown
                      valueType="name"
                      value={bulkVertical}
                      onChange={setBulkVertical}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={updateSelectedLeadVertical}
                    disabled={updatingVertical || (!selectAllFiltered && selectedIds.length === 0)}
                  >
                    {updatingVertical ? 'Updating…' : 'Set vertical'}
                  </Button>
                  <FormControl
                    size="small"
                    sx={{ minWidth: { xs: '100%', sm: 180 }, ...selectFieldBaseSx }}
                  >
                    <InputLabel>Success Post</InputLabel>
                    <Select
                      value={bulkSuccessPost}
                      onChange={(e) => setBulkSuccessPost(e.target.value)}
                      label="Success Post"
                    >
                      <MenuItem value=""><em>Select</em></MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={updateSelectedLeadSuccessPost}
                    disabled={updatingSuccessPost || (!selectAllFiltered && selectedIds.length === 0)}
                  >
                    {updatingSuccessPost ? 'Updating…' : 'Set success post'}
                  </Button>
                  <Tooltip title="Clear the Webhook Sent stamp so selected leads re-deliver to their client webhooks">
                    <span>
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={resendSelectedLeads}
                        disabled={resendingLeads || (!selectAllFiltered && selectedIds.length === 0)}
                      >
                        {resendingLeads ? 'Resending…' : 'Resend leads'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleSelectAllFiltered}
                    disabled={selectAllFiltered || totalCount === 0}
                  >
                    Select all filtered
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClearSelection}
                    disabled={!selectAllFiltered && selectedIds.length === 0}
                  >
                    Clear selection
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={exportLeads}
                    disabled={exporting || (!selectAllFiltered && selectedIds.length === 0)}
                  >
                    {exporting ? 'Exporting…' : 'Export selected'}
                  </Button>
                </Stack>
              </Toolbar>
            ) : (
              <Toolbar
                sx={{
                  px: 0,
                  mb: 1,
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Export only includes leads from your clients and current filters.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={exportLeads}
                  disabled={exporting || totalCount === 0}
                >
                  {exporting ? 'Exporting…' : 'Export filtered'}
                </Button>
              </Toolbar>
            )}
            {exportStatusText ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {exportStatusText}
              </Typography>
            ) : null}
            {exportError ? (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                {exportError}
              </Typography>
            ) : null}

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                <TableRow>
                    {!isClientMember ? (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={!selectAllFiltered && selectedIds.length > 0 && !allOnPageSelected}
                        checked={selectAllFiltered || allOnPageSelected}
                        onChange={handleToggleAllCurrent}
                        inputProps={{ 'aria-label': 'Select all leads on page' }}
                      />
                    </TableCell>
                    ) : null}
                    <TableCell>Client</TableCell>
                    <TableCell>Date Created (EST)</TableCell>
                    <TableCell>Date Sent (EST)</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Territory</TableCell>
                    <TableCell>ZIP Code</TableCell>
                    <TableCell>Self-Book Appt</TableCell>
                    <TableCell>Vertical</TableCell>
                    <TableCell>Processed</TableCell>
                    <TableCell>Success Post</TableCell>
                    <TableCell>Post Response</TableCell>
                    <TableCell>Status Code</TableCell>
                    <TableCell>Sold Date</TableCell>
                    <TableCell>Net Amount</TableCell>
                    <TableCell>Issue</TableCell>






                </TableRow>
                </TableHead>
                <TableBody sx={{ '& td': { py: 1 } }}>
                    {leads.map((lead) => {
                        const toEST = (utc) =>
                        utc ? new Date(utc).toLocaleString('en-US', { timeZone: 'America/New_York' }) : '-';
                        const backend = lead.related_backend_data?.[0];  // Just the most recent match
                        const postResponseText = stringifyValue(lead.post_response);


                        return (
                        <TableRow key={lead.id} hover>
                            {!isClientMember ? (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isRowSelected(lead.id)}
                                onChange={() => handleToggleRow(lead.id)}
                                inputProps={{ 'aria-label': `Select lead ${lead.id}` }}
                              />
                            </TableCell>
                            ) : null}
                            <TableCell>{lead.client_name || '-'}</TableCell>
                            <TableCell>{toEST(lead.date_created)}</TableCell>
                            <TableCell>{toEST(lead.webhook_sent_datetime)}</TableCell>
                            <TableCell>
                            <Link to={`/leads/${lead.id}`} style={{ textDecoration: 'none', color: '#2f80ed' }}>
                                {lead.first_name || 'View'}
                            </Link>
                            </TableCell>
                            <TableCell>{lead.last_name || '-'}</TableCell>
                            <TableCell sx={phoneCellSx}>{lead.phone || '-'}</TableCell>
                            <TableCell>{lead.email || '-'}</TableCell>
                            <TableCell>{lead.source || '-'}</TableCell>
                            <TableCell>{lead.territory || '-'}</TableCell>
                            <TableCell>{lead.zip_code || '-'}</TableCell>
                            <TableCell>{lead.self_book_appointment_date_time}</TableCell>
                            <TableCell>{lead.vertical || '-'}</TableCell>
                            <TableCell>{lead.processed ? '✅' : '❌'}</TableCell>
                            <TableCell>{lead.success_post ? '✅' : '❌'}</TableCell>
                            <TableCell sx={postResponseCellSx}>
                              <Tooltip title={postResponseText} enterDelay={400}>
                                <span style={{ display: 'inline-block', maxWidth: '100%' }}>
                                  {truncate(postResponseText, 120)}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              {lead.last_webhook_status_code != null ? (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    color: statusCodeColor(lead.last_webhook_status_code),
                                    fontWeight: 600,
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {lead.last_webhook_status_code}
                                </Typography>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>{backend?.sold_date || '-'}</TableCell>
                            <TableCell>{backend?.net_amount ? `$${backend.net_amount}` : '-'}</TableCell>
                            <TableCell>{backend?.issue ? '✅' : '❌'}</TableCell>


                        </TableRow>
                        );
                    })}
                    {leads.length === 0 && (
                        <TableRow>
                        <TableCell colSpan={20} align="center">
                            {loading ? 'Loading...' : 'No leads found.'}
                        </TableCell>
                        </TableRow>
                    )}
                    </TableBody>

              </Table>
            </TableContainer>
  
            <Stack direction="row" justifyContent="flex-end" mt={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }
  
